import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment-timezone';
import { Spinner } from 'reactstrap';
import {
  getTeamMemberLastActivityMap,
  getTeamTimelineForDate,
} from '../../services/teamsTimeline';
import styles from './TeamsTimeline.module.scss';
import { Button, buttonVariants } from '@/components/Button';
import { fontWeightEnum, Text, textVariantsEnum } from '@/components/Text';
import { Input } from '@/components/Input';
import { Dropdown, borderedSelectStyles } from '@/components/Dropdown';
import { TableComponent } from '@/components/Table/Table.component';
import { teamsTimelineColumnConfig } from './TeamsTimeline.config';
import { normalizeIdleForDisplay } from '@/utils/idleDisplayUtils';
import {
  buildTeamsTimelineFilterUpdates,
  getInitialTeamsTimelineDateFromSearch,
  getTeamsTimelineFiltersFromSearch,
  normalizeTeamsTimelineStatus,
} from '@/utils/teamsTimelineUrlFilters';
import { mergeSearchParams, normalizeSearchString } from '@/utils/urlSearch';

const ACTIVE_WINDOW_MS = 6 * 60 * 1000;

const toMillisOrNull = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  const parsed = moment(value);
  if (!parsed.isValid()) return null;
  return parsed.valueOf();
};

const isActiveByLastActivity = (lastActivityAt, nowMs) => {
  const lastActivityMs = toMillisOrNull(lastActivityAt);
  if (lastActivityMs == null) return false;
  return nowMs - lastActivityMs <= ACTIVE_WINDOW_MS;
};

const formatSecondsToHHMM = (sec = 0) => {
  const totalMins = Math.floor(Number(sec) / 60);
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const formatTimeInTz = (date, timeZone) => {
  if (!date) return '—';
  return moment(date).tz(timeZone).format('hh:mm A');
};

export const TeamsTimelineComponent = () => {
  const history = useHistory();
  const location = useLocation();
  const team = useSelector((state) => state.team);
  const auth = useSelector((state) => state.auth);

  const teamId =
    team?.selectedTeamID ||
    team?.active?.activeTeamid ||
    team?.teams?.data?.[0]?.activeTeamid;
  const companyId = auth?.profile?.activeCompany?.companyId;

  const [selectedDate, setSelectedDate] = useState(() => {
    const search = typeof window !== 'undefined' ? window.location.search : '';
    return getInitialTeamsTimelineDateFromSearch(search);
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(() => {
    const initialSearch =
      typeof window !== 'undefined' ? window.location.search : '';
    const initialFilters = getTeamsTimelineFiltersFromSearch(initialSearch);
    return normalizeTeamsTimelineStatus(initialFilters.status);
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [statusNowMs, setStatusNowMs] = useState(Date.now());

  const filtersFromUrl = useMemo(
    () => getTeamsTimelineFiltersFromSearch(location.search),
    [location.search]
  );
  const [isRefreshingStatus, setIsRefreshingStatus] = useState(false);

  const selectedTimezone = useMemo(() => {
    if (filtersFromUrl.tz) return filtersFromUrl.tz;
    return moment.tz.guess();
  }, [filtersFromUrl.tz]);

  useEffect(() => {
    const nextDate =
      filtersFromUrl.start &&
      filtersFromUrl.end &&
      filtersFromUrl.start === filtersFromUrl.end
        ? filtersFromUrl.start
        : getInitialTeamsTimelineDateFromSearch(location.search);
    setSelectedDate((prev) => (prev === nextDate ? prev : nextDate));
  }, [filtersFromUrl.start, filtersFromUrl.end, location.search]);

  useEffect(() => {
    setStatusFilter((prev) =>
      prev === filtersFromUrl.status ? prev : filtersFromUrl.status
    );
  }, [filtersFromUrl.status]);

  useEffect(() => {
    if (!teamId || !selectedDate) return;

    const nextSearch = mergeSearchParams(
      location.search,
      buildTeamsTimelineFilterUpdates({
        start: selectedDate,
        end: selectedDate,
        tz: selectedTimezone,
        status: statusFilter,
      })
    );
    if (
      normalizeSearchString(nextSearch) ===
      normalizeSearchString(location.search)
    ) {
      return;
    }

    history.replace({
      pathname: location.pathname,
      search: nextSearch,
    });
  }, [
    history,
    location.pathname,
    location.search,
    selectedDate,
    selectedTimezone,
    statusFilter,
    teamId,
  ]);

  const fetchData = useCallback(
    async ({ silent = false } = {}) => {
      if (!teamId) {
        setError('Please select a team.');
        return;
      }
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      try {
        const result = await getTeamTimelineForDate({
          teamId,
          date: selectedDate,
          companyId,
        });
        if (result.success) {
          setRows(result.data || []);
          setStatusNowMs(Date.now());
        } else {
          setError(result.error || 'Failed to load team timeline');
          if (!silent) setRows([]);
        }
      } catch (err) {
        setError(err?.message || 'Something went wrong');
        if (!silent) setRows([]);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [companyId, selectedDate, teamId]
  );

  const refreshStatusData = useCallback(
    async ({ showIndicator = false } = {}) => {
      if (!teamId) return;
      if (showIndicator) setIsRefreshingStatus(true);
      try {
        const lastActivityMap = await getTeamMemberLastActivityMap(teamId);
        setRows((prevRows) =>
          (prevRows || []).map((row) => ({
            ...row,
            lastActivityAt:
              lastActivityMap?.[row.userUID] || row.lastActivityAt || null,
          }))
        );
        setStatusNowMs(Date.now());
      } catch (error) {
        // Keep current rows if manual status refresh fails.
      } finally {
        if (showIndicator) setIsRefreshingStatus(false);
      }
    },
    [teamId]
  );

  const handleManualStatusRefresh = useCallback(() => {
    refreshStatusData({ showIndicator: true });
  }, [refreshStatusData]);

  useEffect(() => {
    if (teamId && selectedDate) {
      fetchData();
      refreshStatusData();
    } else {
      setRows([]);
      if (!teamId) setError('Please select a team.');
    }
  }, [teamId, selectedDate, fetchData, refreshStatusData]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return (rows || []).filter((r) => {
      const nameMatches = q
        ? (r.userName || '').toLowerCase().includes(q)
        : true;
      if (!nameMatches) return false;

      if (statusFilter === 'all') return true;
      const isActive = isActiveByLastActivity(r.lastActivityAt, statusNowMs);
      return statusFilter === 'active' ? isActive : !isActive;
    });
  }, [rows, searchQuery, statusFilter, statusNowMs]);

  const statusOptions = useMemo(
    () => [
      { value: 'all', label: 'All' },
      { value: 'active', label: 'Active' },
      { value: 'inactive', label: 'Inactive' },
    ],
    []
  );

  const statusEmptyStateMessage =
    statusFilter === 'active'
      ? 'No active users found.'
      : statusFilter === 'inactive'
        ? 'No inactive users found.'
        : '';

  const handleExport = () => {
    const escape = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const header = [
      'Employee Name',
      'Timezone',
      'Check-in',
      'Check-out',
      'Work Time',
      'Productive Time',
      'Idle Time',
    ];
    const dataRows = filteredRows.map((r) => [
      r.userName,
      r.timeZone,
      r.checkIn ? formatTimeInTz(r.checkIn, r.timeZone) : '—',
      r.checkOut ? formatTimeInTz(r.checkOut, r.timeZone) : '—',
      formatSecondsToHHMM(r.workSec),
      formatSecondsToHHMM(r.productiveSec),
      formatSecondsToHHMM(
        normalizeIdleForDisplay(r.workSec, r.idleSec).displayIdle
      ),
    ]);
    const lines = [
      header.join(','),
      ...dataRows.map((row) => row.map((cell) => escape(cell)).join(',')),
    ];
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `team_timeline_${selectedDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className={styles.teamsTimelinePage}>
        <div className={styles.teamsTimelineContainer}>
          <div className={styles.teamsTimelineHeader}>
            <Text
              variant={textVariantsEnum.Header}
              fontWeight={fontWeightEnum.Normal}
            >
              Department Timeline
            </Text>
            <div className={styles.buttonsContainer}>
              <Button
                text={'Apply'}
                variant={buttonVariants.primary}
                onClick={fetchData}
                disabled={!teamId || loading}
                style={{
                  cursor: teamId && !loading ? 'pointer' : 'not-allowed',
                }}
              />
              <Button
                text={'Export'}
                onClick={handleExport}
                variant={buttonVariants.secondary}
                disabled={loading || filteredRows.length === 0}
                style={{
                  cursor:
                    !loading && filteredRows.length ? 'pointer' : 'not-allowed',
                }}
              />
            </div>
          </div>
          <div className={styles.teamsTimelineContent}>
            <div className={styles.filters}>
              <Input
                className={styles.filter}
                label={'Date'}
                type="date"
                placeholder="Search employees..."
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
              <Input
                className={styles.filter}
                label={'Search'}
                type="text"
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Dropdown
                label="Status"
                options={statusOptions}
                value={statusOptions.find(
                  (option) => option.value === statusFilter
                )}
                onChange={(option) => setStatusFilter(option?.value || 'all')}
                classNamePrefix="status-filter"
                selectStyles={borderedSelectStyles}
                width={280}
              />
            </div>
          </div>
          {loading && (
            <div className="text-center my-4 w-100">
              <Spinner color="primary" />
              <p className="mt-2 text-muted mb-0">Loading team timeline…</p>
            </div>
          )}
          {!loading &&
            searchQuery.trim() &&
            filteredRows.length === 0 &&
            rows.length > 0 && (
              <div className="text-center text-muted py-3 w-100">
                No employees match &quot;{searchQuery}&quot;.
              </div>
            )}
          {!loading &&
            !searchQuery.trim() &&
            statusFilter !== 'all' &&
            filteredRows.length === 0 && (
              <div className="text-center text-muted py-4 w-100">
                {statusEmptyStateMessage}
              </div>
            )}
          {!loading && !rows.length && teamId && statusFilter === 'all' && (
            <div className="text-center text-muted py-4 w-100">
              No members or no sessions for this date.
            </div>
          )}
          {error && (
            <div className="alert alert-warning mb-3 w-100" role="alert">
              {error}
            </div>
          )}

          {!loading && filteredRows.length > 0 && (
            <TableComponent
              className={styles.teamsTimelineTable}
              columns={teamsTimelineColumnConfig({
                nowMs: statusNowMs,
                onRefreshStatus: handleManualStatusRefresh,
                isRefreshingStatus,
              })}
              data={filteredRows}
            />
          )}
        </div>
      </div>
    </>
  );
};
